import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  SimpleChanges,
  ChangeDetectorRef
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl
} from '@angular/forms';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  firstValueFrom,
  Subject,
  takeUntil,
  debounceTime,
  distinctUntilChanged
} from 'rxjs';
import { CommonServiceService } from 'src/app/Services/common-service.service';
import { CustomToastsService } from 'src/app/Services/custom-toasts.service';
import {
  LicensePlateAssociationName,
  LicensePlateStatus
} from 'src/app/Services/enum';
import {
  LicenseplateItems,
  LicensePlates,
  ProductionOverride,
  ProductionOverrideLicensePlates,
  Stores,
  WarehouseLocationsModels,
  WarehouseModel
} from '../../../Models/ApplicationModels';
import { MasterDataSeriveService } from '../../../Services/master-data-serive.service';
import { OverrideType } from 'src/app/enum/enum';
import { WeekColorService } from '../../admin-module/company-config/WeekColors/services/week-color.service';
import {
  ColorSchedule,
  ProductionCycleSchedule
} from '../../admin-module/company-config/WeekColors/models/week-color.model';

export interface OverrideState {
  isActive: boolean;
  overrideId?: number;
  date?: string;
  weekColor?: string;
  weekColorName?: string;
  location?: number;
  licensePlateId?: number;
  licensePlateDesc?: string;
  licensePlateStoreId?: number;
  overrideDesc?: string;
}

interface TouchedOverrideLicensePlate {
  overrideId: number;
  licensePlateId: number;
}

@Component({
  selector: 'app-production-override',
  templateUrl: './production-override.component.html',
  styleUrls: ['./production-override.component.css']
})
export class ProductionOverrideComponent implements OnInit, OnDestroy {
  @ViewChild('weekColorDate') weekColorDate!: ElementRef;

  @Input() stores: Stores[] = [];
  @Input() productionStore: number = 0;
  @Input() productionStoreName: string = '';
  @Input() companyId: number = 0;

  @Output() overrideStateChanged = new EventEmitter<OverrideState>();
  @Output() storeChanged = new EventEmitter<number>();
  @Output() needsRefresh = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private userDetails = this.masterService.getUserDetailsFromStorage();

  // Reactive Forms
  overrideForm!: FormGroup;
  licensePlateForm!: FormGroup;

  // Override properties
  selectedOverride: ProductionOverride = new ProductionOverride();
  selectedOverrideId: number = 0;
  productionOverrideList: ProductionOverride[] = [];
  filteredOverrideList: ProductionOverride[] = [];
  selectedBgColor: string = '';
  isWeekDateSet: boolean = false;
  minReminderDate: string = '';
  OverrideTypes = OverrideType;

  // Week Color properties
  weekColorSchedule: Array<ProductionCycleSchedule> = [];
  selectedWeekColorObj: ProductionCycleSchedule | null = null;

  // License Plate properties
  licensePlates: LicensePlates[] = [];
  emptyLicensePlates: LicensePlates[] = [];
  licenseplate: LicensePlates = new LicensePlates();
  associateLicensePlate: ProductionOverrideLicensePlates =
    new ProductionOverrideLicensePlates();
  selectedPlate: string = '0';
  isAddingNew: boolean = false;
  associateEmptyLp: boolean = false;
  isLoadingLicensePlates: boolean = false;
  licensePlateCount: number = 0;
  licensePlateStoreId: number = 0;

  // Location properties
  locations: WarehouseLocationsModels[] = [];
  warehouses: WarehouseModel[] = [];

  // Store properties
  overrideStore: number = 0;

  // User properties
  overridePermission: boolean = false;
  isOverride: boolean = false;
  showWeeklyOverrideOption: boolean = true;

  overrideDetails: OverrideState = {
    isActive: false
  };

  get isEditMode(): boolean {
    return (this.selectedOverride?.id ?? 0) > 0;
  }

  private isLicensePlateSelected(): boolean {
    return (
      this.selectedPlate !== '0' &&
      this.selectedPlate !== 'new' &&
      this.selectedPlate !== 'empty'
    );
  }

  get isFormValid(): boolean {
    const formAndDateValid = this.overrideForm?.valid && this.isWeekDateValid();

    if (this.isEditMode) {
      return formAndDateValid && this.isLicensePlateSelected();
    }

    return formAndDateValid;
  }

  constructor(
    private fb: FormBuilder,
    private masterService: MasterDataSeriveService,
    private toastr: CustomToastsService,
    private permissionsService: NgxPermissionsService,
    private commonService: CommonServiceService,
    private weekColorService: WeekColorService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    const productionScheduleType = (this.userDetails?.productionScheduleType ?? '')
      .toString()
      .trim()
      .toLowerCase();
    this.showWeeklyOverrideOption = productionScheduleType !== 'datespecific';

    this.minReminderDate = this.getFutureDates();
    this.initializeForms();
    this.setupFormSubscriptions();
    this.getWeekColorsSchedule();
    this.isOverride = localStorage.getItem('isOverride') === 'true';
    const storedDetails = localStorage.getItem('overrideDetails');
    if (storedDetails) {
      this.overrideDetails = JSON.parse(storedDetails);
      this.selectedOverrideId = this.overrideDetails.overrideId || 0;
    }

    if (this.isOverride) {
      this.getExistingOverride();
    }
    window.addEventListener(
      'licensePlateCountUpdated',
      this.onLicensePlateCountUpdated as EventListener
    );
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (
      changes['productionStore'] &&
      changes['productionStore'].currentValue > 0
    ) {
      const storeId = changes['productionStore'].currentValue;

      await this.getLocationforLP(storeId);
      this.loadWarehouses(storeId);
    }
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    try {
      window.removeEventListener(
        'licensePlateCountUpdated',
        this.onLicensePlateCountUpdated as EventListener
      );
    } catch (e) { }
  }

  getAllOverrides() {
    this.masterService
      .getProductionOverrides(this.companyId, { sourceStoreId: this.productionStore })
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (res) => {
        let overrides = res as ProductionOverride[];
        this.productionOverrideList = overrides;
        if (this.selectedOverrideId > 0) {
          this.setOverrideStore();
          const existingOverride = overrides.find(
            (o) => o.id === this.selectedOverrideId
          );
          if (existingOverride) {
            this.selectedOverride = existingOverride;
            this.populateForm(existingOverride);
            this.getAllLicensePlates();
          }
        }
      });
  }

  private initializeForms(): void {
    this.overrideForm = this.fb.group({
      description: ['', [Validators.required, Validators.maxLength(50)]],
      destinationStoreId: [0, [Validators.required, Validators.min(1)]],
      sourceStoreId: [0],
      weekColorDate: ['', [Validators.required]],
      setReminder: [false],
      reminderDate: [''],
      weekColor: [''],
      type: [OverrideType.DateSpecific, Validators.required]
    });

    this.licensePlateForm = this.fb.group({
      description: ['', Validators.required],
      warehouseLocationId: [0, [Validators.required, Validators.min(1)]]
    });
  }

  private setupFormSubscriptions(): void {
    // Watch week color date changes
    this.overrideForm
      .get('weekColorDate')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(300)
      )
      .subscribe((date) => {
        if (date) this.handleWeekColorDateChange(date);
      });

    // Watch reminder toggle
    this.overrideForm
      .get('setReminder')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        const reminderCtrl = this.overrideForm.get('reminderDate');

        if (enabled) {
          reminderCtrl?.setValidators([
            Validators.required,
            this.futureDateValidator
          ]);
        } else {
          reminderCtrl?.clearValidators();
        }

        reminderCtrl?.updateValueAndValidity({ emitEvent: false });
      });
  }

  private futureDateValidator = (control: AbstractControl) => {
    if (!control.value) return null;

    // Parse YYYY-MM-DD as local date (not UTC) to avoid timezone shift
    const [y, m, d] = (control.value as string).split('-').map(Number);
    const selected = new Date(y, m - 1, d);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return selected >= today ? null : { notFutureDate: true };
  };

  private handleWeekColorDateChange(
    date: string,
    colorSchedule?: ColorSchedule | null
  ): void {
    if (!this.showWeeklyOverrideOption) {
      this.overrideForm.patchValue(
        {
          weekColorDate: date
        },
        { emitEvent: false }
      );
      this.cdr.detectChanges();
      return;
    }

    // Use the passed colorSchedule (from API response) if available
    const schedule =
      colorSchedule ?? this.selectedOverride?.colorSchedule ?? null;

    if (schedule?.color && schedule?.displayName) {
      // Use colorSchedule data from the override response directly — no API call needed
      this.selectedBgColor = schedule.color;
      this.isWeekDateSet = true;
      this.overrideForm.patchValue(
        {
          weekColor: schedule.displayName,
          weekColorDate: date
        },
        { emitEvent: false }
      );
      this.cdr.detectChanges();
      localStorage.setItem('FutureProductionCycle', JSON.stringify(schedule));
    } else {
      // Fallback: fetch color schedule from API when not present in response
      this.weekColorService
        .getProductionCycleScheduleByDate(this.companyId, date)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (weekColor) => {
            const wc = weekColor as ColorSchedule;
            this.selectedBgColor = wc.color;
            this.isWeekDateSet = true;
            this.overrideForm.patchValue(
              {
                weekColor: wc.displayName,
                weekColorDate: date
              },
              { emitEvent: false }
            );
            this.cdr.detectChanges();
            this.selectedOverride.weekColor = wc.displayName;
            this.selectedOverride.weekColorDate = date;
            localStorage.setItem('FutureProductionCycle', JSON.stringify(wc));
          },
          () => this.toastr.error('Failed to load week color')
        );
    }
  }

  private isWeekDateValid(): boolean {
    const weekColorDate = this.overrideForm.get('weekColorDate')?.value;
    if (!weekColorDate) return false;

    const today = this.getFutureDates();
    return weekColorDate >= today;
  }

  private getFutureDates(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onLicensePlateCountUpdated = (e: any) => {
    try {
      const detail = e?.detail;
      if (!detail) return;
      const lpId = detail.licensePlateId;
      const count = detail.count;
      const obj = localStorage.getItem('overrideDetails');
      if (obj) {
        const override = JSON.parse(obj);
        if (override.licensePlateId == lpId) {
          this.licensePlateCount = count;
          localStorage.setItem('licensePlateCount', count.toString());
        }
      }
    } catch (err) { }
  };

  selectOverride() {
    const storedDetails = localStorage.getItem('overrideDetails');
    const previousDetails = storedDetails
      ? (JSON.parse(storedDetails) as OverrideState)
      : null;
    const previousOverrideId = previousDetails?.overrideId ?? 0;
    const previousLicensePlateId = previousDetails?.licensePlateId ?? 0;

    let item = this.productionOverrideList.find(
      (item) => item.id == this.selectedOverrideId
    );

    if (item) {
      if (
        this.isOverride &&
        previousLicensePlateId > 0 &&
        previousOverrideId !== item.id
      ) {
        // Release the LP tied to the override being switched away from -
        // otherwise it stays InUse in IMS forever (this was the root cause of the bug).
        this.updateLicensePlateStatus(
          previousLicensePlateId,
          LicensePlateStatus.Available
        );
        this.removeTouchedOverrideLicensePlate(previousOverrideId);
      }

      this.selectedOverride = item;
      this.populateForm(item);
      this.selectedPlate = '0';
      this.licensePlates = [];
      this.getAllLicensePlates();
    }
  }

  private async populateForm(override: ProductionOverride): Promise<void> {
    this.overrideForm.patchValue(
      {
        description: override.description,
        destinationStoreId: override.destinationStoreId,
        sourceStoreId: override.sourceStoreId ?? 0,
        weekColorDate: override.weekColorDate,
        reminderDate: override.reminderDate ?? null,
        weekColor: override.weekColor,
        type: override.type
      },
      { emitEvent: false }
    );
    this.cdr.detectChanges();
    this.overrideForm.patchValue({ setReminder: !!override.reminderDate });

    if (override.weekColorDate) {
      // Pass colorSchedule from the override response directly to avoid redundant API calls
      this.handleWeekColorDateChange(
        override.weekColorDate,
        override.colorSchedule ?? null
      );
    }
    if (this.showWeeklyOverrideOption && override.type === OverrideType.WeeklySchedule) {
      if (override.colorSchedule) {
        // Ensure the weekColorSchedule list is loaded before trying to match
        if (this.weekColorSchedule.length === 0) {
          await this.getWeekColorsSchedule();
        }
        // Match the saved colorSchedule (by its id = productionCycleScheduleId) to a ProductionCycleSchedule entry
        this.selectedWeekColorObj =
          this.weekColorSchedule.find(
            (w) =>
              w.productionCycleId === override.colorSchedule!.productionCycleId
          ) ?? null;
        this.selectedBgColor = override.colorSchedule.color || '';
      }
    }
  }

  async addProductionOverride(overrideObj: ProductionOverride) {
    overrideObj.companyId = this.companyId;
    overrideObj.sourceStoreId = this.productionStore;

    await this.masterService
      .addProductionOverride(overrideObj)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          if (res) {
            this.toastr.success('Override created successfully');
            this.selectedOverride = res as ProductionOverride;
            this.selectedOverrideId = this.selectedOverride.id;

            // First, determine which store's default location to use
            const selectedWarehouseLocationId =
              this.userDetails.storeId == this.productionStore
                ? this.userDetails.defaultProcessorLocationID
                : this.stores.find((s) => s.storeId === this.productionStore)
                  ?.defaultWarehouseLocationId;

            // Try to find "Producer Bin" first, if not found, fall back to the default location
            const producerBinLocation = this.locations.find(
              (x) => x.name === 'Producer Bin'
            )?.id;
            const defaultLocationId =
              producerBinLocation ?? selectedWarehouseLocationId ?? 0;

            this.licensePlateForm.patchValue({
              warehouseLocationId: defaultLocationId ?? 0,
              description: overrideObj.description
            });

            this.addNewPlate();
          }
        },
        (error) => {
          this.resetForm();
          const errorMessage = this.commonService.extractErrorMessage(
            error,
            'Something went wrong while creating override'
          );
          this.toastr.error(errorMessage);
        }
      );
  }

  updateProductionOverride(
    overrideId: number,
    overrideObj: ProductionOverride
  ) {
    overrideObj.sourceStoreId = this.productionStore;
    this.masterService
      .updateProductionOverride(overrideId, overrideObj)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          if (res) {
            this.toastr.success('Override has been updated successfully');
            this.getAllOverrides();
          }
        },
        (error) => {
          const errorMessage = this.commonService.extractErrorMessage(
            error,
            'Something went wrong while updating override'
          );
          this.toastr.error(errorMessage);
        }
      );
  }

  checkNewOption() {
    if (this.selectedPlate === 'new') {
      this.isAddingNew = true;
      this.associateEmptyLp = false;
      this.licensePlateForm.reset({
        description: '',
        warehouseLocationId: 0
      });
      this.licenseplate = new LicensePlates();
    } else if (this.selectedPlate === 'empty') {
      this.associateEmptyLp = true;
      this.isAddingNew = false;
      this.licensePlateForm.reset({
        description: '',
        warehouseLocationId: 0
      });
      this.getEmptyLicensePlate();
    } else {
      this.isAddingNew = false;
      this.associateEmptyLp = false;
      // The previously-selected LP is released atomically by updateOverrideState()'s
      // Activate call once the user proceeds with the newly-selected plate.
      this.licenseplate =
        this.licensePlates.find(
          (license) => license.id == parseInt(this.selectedPlate)
        ) || new LicensePlates();

      if (this.licenseplate.id > 0) {
        this.licensePlateForm.patchValue({
          description: this.licenseplate.description || '',
          warehouseLocationId: this.licenseplate.warehouseLocationId || 0
        });
      }
    }
  }

  async getEmptyLicensePlate() {
    this.emptyLicensePlates = await firstValueFrom(
      this.masterService.getEmptyLicensePlate(this.companyId)
    );
    this.emptyLicensePlates = this.emptyLicensePlates.filter(
      (lp) =>
        lp.status === LicensePlateStatus.Available &&
        this.locations.some((loc) => loc.id === lp.warehouseLocationId)
    );
  }

  getAllLicensePlates() {
    this.masterService
      .getAllLicensePlates(this.companyId, this.selectedOverride.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.licensePlates = res as LicensePlates[];
          this.licensePlates = this.licensePlates.filter(
            (lp) =>
              (lp.status === LicensePlateStatus.InUse ||
                lp.status === LicensePlateStatus.Available) &&
              this.locations.some((loc) => loc.id === lp.warehouseLocationId)
          );

          this.selectLicensePlate();
        }
      });
  }

  selectLicensePlate() {
    if (this.selectedPlate !== '0') {
      const lpExists = this.licensePlates.some(
        (lp) => lp.id.toString() === this.selectedPlate
      );

      if (lpExists) {
        this.isAddingNew = false;
        this.licenseplate =
          this.licensePlates.find(
            (lp) => lp.id.toString() === this.selectedPlate
          ) || new LicensePlates();
      } else {
        const firstAvailableLp = this.licensePlates.find(
          (lp) => lp.status === LicensePlateStatus.Available
        );

        if (firstAvailableLp) {
          this.selectedPlate = firstAvailableLp.id.toString();
          this.licenseplate = firstAvailableLp;
          this.isAddingNew = false;
        } else if (this.licensePlates.length > 0) {
          this.selectedPlate = this.licensePlates[0].id.toString();
          this.licenseplate = this.licensePlates[0];
          this.isAddingNew = false;
        } else {
          // No LPs exist — auto-trigger Add New
          this.selectedPlate = 'new';
          this.licenseplate = new LicensePlates();
          this.isAddingNew = true;
          this.associateEmptyLp = false;
          this.licensePlateForm.reset({
            description: '',
            warehouseLocationId: 0
          });
        }
      }
    } else {
      const firstAvailableLp = this.licensePlates.find(
        (lp) => lp.status === LicensePlateStatus.Available
      );

      if (firstAvailableLp) {
        this.selectedPlate = firstAvailableLp.id.toString();
        this.licenseplate = firstAvailableLp;
        this.isAddingNew = false;
      } else if (this.licensePlates.length > 0) {
        this.selectedPlate = this.licensePlates[0].id.toString();
        this.licenseplate = this.licensePlates[0];
        this.isAddingNew = false;
      } else {
        this.selectedPlate = 'new';
        this.licenseplate = new LicensePlates();
        this.isAddingNew = true;
        this.associateEmptyLp = false;
        this.licensePlateForm.reset({
          description: '',
          warehouseLocationId: 0
        });
      }
    }
  }
  /**
   * Explicitly sets (or clears) the active override before a scanned LP is
   * assigned. Callers must resolve the correct override (or null) themselves
   * — this never guesses from cached state, so an LP with no real override
   * association can never end up attached to an unrelated cached one.
   */
  public async adoptOverride(
    override: ProductionOverride | null
  ): Promise<void> {
    if (override) {
      this.selectedOverride = override;
      this.selectedOverrideId = override.id;
      if (override.weekColorDate) {
        await this.populateForm(override);
      }
    } else {
      this.selectedOverride = new ProductionOverride();
      this.selectedOverrideId = 0;
    }
  }

  public async assignLicensePlateFromScan(
    licensePlate: LicensePlates
  ): Promise<void> {
    if (!licensePlate || !licensePlate.id) {
      this.toastr.error('Invalid license plate selected');
      return;
    }

    this.licenseplate = licensePlate;
    this.selectedPlate = licensePlate.id.toString();

    if (!this.licensePlates.some((lp) => lp.id === licensePlate.id)) {
      this.licensePlates = [licensePlate, ...this.licensePlates];
    }

    this.licensePlateForm.patchValue({
      description: licensePlate.description || '',
      warehouseLocationId: licensePlate.warehouseLocationId || 0
    });

    if (licensePlate.warehouseLocationId != null) {
      localStorage.setItem(
        'CurrentLocationID',
        licensePlate.warehouseLocationId.toString()
      );
    }

    // updateOverrideState() activates this plate and releases whatever was
    // previously InUse for this override's store atomically via the backend.
    await this.updateOverrideState();
  }

  /**
   * Determines the store this LP should be attributed to for production
   * (used for `productionStoreId` on the produce-item request). This is
   * independent of whether the LP has a ProductionOverride association —
   * it never looks up or matches an override, it only resolves a store id.
   */
  public async resolveLicensePlateStoreId(
    licensePlate: LicensePlates
  ): Promise<void> {
    const belongsToCurrentStore = this.locations.some(
      (loc) => loc.id === licensePlate.warehouseLocationId
    );
    if (belongsToCurrentStore) {
      this.licensePlateStoreId = 0;
      return;
    }

    try {
      const allLocations = (await firstValueFrom(
        this.masterService.getWarehouseLocations(this.companyId, null)
      )) as WarehouseLocationsModels[];
      const lpLocation = allLocations.find(
        (loc) => loc.id === licensePlate.warehouseLocationId
      );
      this.licensePlateStoreId = lpLocation?.storeId || 0;
    } catch (err) {
      console.error('Failed to resolve license plate store', err);
      this.licensePlateStoreId = 0;
    }
  }

  async addNewPlate() {
    if (!this.licensePlateForm.valid) {
      this.toastr.error('Please fill all required fields for license plate');
      return;
    }

    this.isLoadingLicensePlates = true;
    this.licensePlateStoreId = 0;

    const lpFormValue = this.licensePlateForm.value;

    // Build the license plate object from form values
    const newLicensePlate: LicensePlates = {
      id: 0,
      description: lpFormValue.description,
      warehouseLocationId: lpFormValue.warehouseLocationId,
      associationId: this.selectedOverride.id,
      associationName: LicensePlateAssociationName.Store,
      companyId: this.companyId,
      status: LicensePlateStatus.Available,
      referenceNumber: this.productionStoreName,
      createdAt: '',
      createdById: 0,
      lastModifiedAt: '',
      lastModifiedById: 0
    };

    this.masterService
      .addLicensePlate(newLicensePlate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (res) => {
          if (res) {
            this.toastr.success('License plate has been added successfully');
            this.licenseplate = res as LicensePlates;
            this.getAllLicensePlates();
            this.selectedPlate = this.licenseplate.id.toString();
            this.isAddingNew = false;

            const lpId = this.licenseplate.id;
            if (lpId) {
              localStorage.setItem(`licensePlateCount_${lpId}`, '0');
              localStorage.setItem('licensePlateCount', '0');
            }

            // Activates this new plate and releases whatever was previously
            // InUse for this override's store atomically via the backend.
            await this.updateOverrideState();
          }
          this.isLoadingLicensePlates = false;
        },
        error: (err) => {
          this.resetForm();
          console.error('Error adding new license plate', err);
          this.isLoadingLicensePlates = false;
          this.toastr.error('Failed to add license plate');
        }
      });
  }

  canAddLicensePlate(): boolean {
    return this.licensePlateForm.valid;
  }

  public async updateLicensePlateStatusIfEmpty(
    licenseplate: LicensePlates
  ): Promise<void> {
    licenseplate.status = LicensePlateStatus.Available;
    licenseplate.associationId = this.selectedOverride.id;
    licenseplate.associationName = LicensePlateAssociationName.Store;

    let LpId = 0;
    const override = localStorage.getItem('overrideDetails');

    if (override) {
      const over = JSON.parse(override);
      LpId = over?.licensePlateId ?? 0;
    }

    if (LpId) {
      this.updateLicensePlateStatus(LpId, LicensePlateStatus.Available);
    }

    this.masterService
      .UpdateLicensePlate(licenseplate.id, licenseplate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.getAllLicensePlates();
          this.associateEmptyLp = false;
        },
        error: (err) => {
          const errorMessage = this.commonService.extractErrorMessage(
            err,
            'Failed to update license plate'
          );
          this.toastr.error(errorMessage);
        }
      });
  }

  async getLocationforLP(storeId: number) {
    const userDetails = this.masterService.getUserDetailsFromStorage();
    const response: any = await this.masterService
      .getWarehouseLocations(userDetails.companyID, storeId)
      .toPromise();
    if (response) this.locations = response as WarehouseLocationsModels[];
  }

  openOverrideDatePicker() {
    const dateInput = document.getElementById(
      'weekColorDate'
    ) as HTMLInputElement;
    if (dateInput) {
      dateInput.showPicker();
    }
  }
  openDatePicker(dateInput: HTMLInputElement): void {
    dateInput?.showPicker?.();
  }

  openReminderDatePicker() {
    const dateInput = document.getElementById(
      'reminderDateInput'
    ) as HTMLInputElement;
    if (dateInput) {
      dateInput.showPicker();
    }
  }

  setWeekDate() {
    const weekColorDate = this.overrideForm.get('weekColorDate')?.value;
    if (weekColorDate) {
      localStorage.setItem('WeekDate', weekColorDate);
      this.handleWeekColorDateChange(weekColorDate);
    }
  }

  getWeekDate() {
    const weekDate = this.selectedOverride.weekColorDate;
    if (weekDate) {
      this.handleWeekColorDateChange(weekDate);
    }
  }

  async toggleOverrideOff() {
    this.resetForm();
    this.isOverride = false;
    this.isWeekDateSet = false;
    this.overrideDetails = { isActive: false };
    this.selectedOverrideId = 0;
    this.selectedPlate = '0';
    this.licensePlates = [];
    this.overrideStore = 0;
    this.licensePlateStoreId = 0;
    this.initializeForms();

    // Clear localStorage
    localStorage.removeItem('isOverride');
    localStorage.removeItem('MainOverrides');
    localStorage.removeItem('WeekDate');
    localStorage.removeItem('FutureProductionCycle');

    try {
      const keys = Object.keys(localStorage);
      keys.forEach((k) => {
        if (k && k.startsWith('licensePlateCount_')) {
          localStorage.removeItem(k);
        }
      });
    } catch (e) { }

    localStorage.removeItem('licensePlateCount');
    localStorage.removeItem('CurrentLocationID');

    // Release every override/LP pair touched this session, not just the last
    // one - overrideDetails only ever holds the most recently activated pair.
    const touchedLicensePlates = this.getTouchedOverrideLicensePlates();
    touchedLicensePlates.forEach((touched) => {
      this.updateLicensePlateStatus(
        touched.licensePlateId,
        LicensePlateStatus.Available
      );
    });
    localStorage.removeItem('touchedOverrideLicensePlates');

    localStorage.removeItem('overrideDetails');

    const selectedWarehouseLocationId =
      this.userDetails.storeId == this.productionStore
        ? this.userDetails.defaultProcessorLocationID
        : this.stores.find((s) => s.storeId === this.productionStore)
          ?.defaultWarehouseLocationId;
    if (selectedWarehouseLocationId) {
      localStorage.setItem(
        'CurrentLocationID',
        selectedWarehouseLocationId?.toString() || ''
      );
    }

    // Emit state change
    this.overrideStateChanged.emit({ isActive: false });
    this.needsRefresh.emit();
  }

  private buildOverridePayload(): ProductionOverride {
    const formValue = this.overrideForm.value;

    return {
      ...this.selectedOverride,
      description: formValue.description,
      destinationStoreId: formValue.destinationStoreId,
      sourceStoreId: formValue.sourceStoreId || this.productionStore,
      weekColorDate: formValue.weekColorDate,
      weekColor: formValue.weekColor,
      reminderDate: formValue.setReminder ? formValue.reminderDate : null,
      companyId: this.companyId,
      type: formValue.type ?? OverrideType.DateSpecific
    };
  }

  onSetReminderChange(): void {
    const setReminder = this.overrideForm.get('setReminder')?.value;
    const reminderDateCtrl = this.overrideForm.get('reminderDate');

    if (!setReminder) {
      reminderDateCtrl?.setValue(null);
    } else {
      if (!reminderDateCtrl?.value) {
        reminderDateCtrl?.setValue(this.getFutureDates());
      }
    }
  }

  async toggleOverride() {
    if (!this.isFormValid) {
      this.toastr.error('Please fill all required fields correctly');
      return;
    }

    this.licensePlateStoreId = 0;
    const payload = this.buildOverridePayload();

    if (!this.overrideStore || this.overrideStore === 0) {
      if (payload.id == 0) {
        // Creating new override - LP will be created
        await this.addProductionOverride(payload);
      } else {
        // Updating existing override
        await this.updateProductionOverride(payload.id, payload);

        const selectedWarehouseLocationId =
          this.licenseplate.warehouseLocationId;
        if (selectedWarehouseLocationId != null) {
          localStorage.setItem(
            'CurrentLocationID',
            selectedWarehouseLocationId.toString()
          );
        }
        await this.updateOverrideState();
      }
    } else {
      await this.updateOverrideState();
    }
  }
  async checkOverridePermission() {
    this.overridePermission = await this.permissionsService.hasPermission([
      'WeekColor:Override'
    ]);
  }

  async getExistingOverride() {
    const override = localStorage.getItem('MainOverrides');

    if (override) {
      await this.checkOverridePermission();
      const over = JSON.parse(override) as ProductionOverride;
      this.selectedOverride = over;
      this.selectedOverrideId = over.id;

      if (!this.overridePermission) {
        this.overrideStore = over.destinationStoreId;
      }

      if (over.weekColorDate) {
        this.populateForm(over);
      }
    }

    const overrideDetails = localStorage.getItem('overrideDetails');
    if (overrideDetails) {
      const details = JSON.parse(overrideDetails);
      if (details.licensePlateId) {
        this.selectedPlate = details.licensePlateId.toString();
      }
    }

    this.licensePlates = [];
    this.getAllLicensePlates();
    this.getCountOfLicensePlateItem();
  }

  getCountOfLicensePlateItem() {
    const obj = localStorage.getItem('overrideDetails');
    if (!obj) return;

    const override = JSON.parse(obj);
    const licensePlateId = override.licensePlateId;

    if (licensePlateId) {
      const cached = localStorage.getItem(
        `licensePlateCount_${licensePlateId}`
      );
      if (cached != null) {
        this.licensePlateCount = parseInt(cached, 10) || 0;
        localStorage.setItem(
          'licensePlateCount',
          this.licensePlateCount.toString()
        );
        this.overrideStateChanged.emit(this.overrideDetails);
        return;
      }
    }

    if (licensePlateId) {
      this.masterService
        .getItemOfLicensePlate(licensePlateId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (res) {
            const response = res as LicenseplateItems[];
            this.licensePlateCount = response.reduce(
              (total, item) => total + (item.quantity ?? 0),
              0
            );
            localStorage.setItem(
              `licensePlateCount_${licensePlateId}`,
              this.licensePlateCount.toString()
            );
            localStorage.setItem(
              'licensePlateCount',
              this.licensePlateCount.toString()
            );
            this.overrideStateChanged.emit(this.overrideDetails);
          }
        });
    }
  }

  isValidated(): boolean {
    return this.isFormValid;
  }

  setOverrideStore() {
    if (this.overrideStore > 0) {
      this.filteredOverrideList = this.productionOverrideList.filter(
        (x) => x.destinationStoreId === this.overrideStore
      );
      this.licensePlates = [];
    } else {
      this.filteredOverrideList = [...this.productionOverrideList];
    }
  }

  closeFormModal() {
    if (this.isOverride) return;
    this.resetForm();
  }
  resetForm(): void {
    this.overrideForm.reset({
      description: '',
      destinationStoreId: 0,
      sourceStoreId: 0,
      weekColorDate: '',
      setReminder: false,
      reminderDate: '',
      weekColor: '',
      type: OverrideType.DateSpecific
    });

    this.licensePlateForm.reset({
      description: '',
      warehouseLocationId: 0
    });

    this.selectedOverride = new ProductionOverride();
    this.selectedOverrideId = 0;
    this.selectedPlate = '0';
    this.isAddingNew = false;
    this.associateEmptyLp = false;
    this.selectedBgColor = '';
    this.isWeekDateSet = false;
    this.licensePlates = [];
    this.licenseplate = new LicensePlates();
    this.selectedWeekColorObj = null;
  }
  onOverrideTypeChange(): void {
    const type = this.overrideForm.get('type')?.value;

    this.overrideForm.patchValue(
      {
        weekColor: '',
        weekColorDate: ''
      },
      { emitEvent: false }
    );
    this.cdr.detectChanges();
    this.selectedBgColor = '';
    this.selectedWeekColorObj = null;

    if (type === OverrideType.WeeklySchedule) {
      if (this.weekColorSchedule.length === 0) {
        this.getWeekColorsSchedule();
      }
    }
  }

  onWeekColorSelected(selectedColor: ProductionCycleSchedule | null): void {
    if (selectedColor && selectedColor.startDate && selectedColor.displayName) {
      this.overrideForm.patchValue(
        {
          weekColor: selectedColor.displayName,
          weekColorDate: new Date(selectedColor.endDate) //need to change to start date of the week color schedule
            .toISOString()
            .split('T')[0]
        },
        { emitEvent: false }
      );
      this.cdr.detectChanges();
      this.selectedBgColor = selectedColor.color;
    }
  }

  async getWeekColorsSchedule(): Promise<void> {
    if (!this.showWeeklyOverrideOption) return;
    try {
      const res = await this.weekColorService
        .getProductionCycleSchedule(this.userDetails.companyID)
        .toPromise();

      this.weekColorSchedule = res as ProductionCycleSchedule[];
    } catch (error) {
      console.error('Failed to load week color schedule', error);
    }
  }
  getWarehouseName(warehouseId: number): string {
    const warehouse = this.warehouses.find((w) => w.id === warehouseId);
    return warehouse ? warehouse.name : '';
  }
  loadWarehouses(storeId: number): void {
    this.masterService
      .getWarehousesLite(this.userDetails.companyID, storeId)
      .subscribe({
        next: (res) => {
          this.warehouses = res as WarehouseModel[];
          this.locations = this.locations.filter((loc) =>
            this.warehouses.some((w) => w.id === loc.warehouseId)
          );
        },
        error: () => {
          this.toastr.error('Failed to load warehouses');
        }
      });
  }
  onOffcanvasOpened() {
    this.getAllOverrides();
  }
  private async updateOverrideState(): Promise<void> {
    const payload = this.buildOverridePayload();

    if (this.selectedOverrideId && this.licenseplate.id) {
      // Activates this plate and releases whatever other plate is InUse for
      // this override's company/source store, atomically, on the backend.
      const activated = await this.activateLicensePlateForOverride(
        this.selectedOverrideId,
        this.licenseplate.id
      );
      if (!activated) {
        return;
      }
    }

    localStorage.setItem(
      'MainOverrides',
      JSON.stringify(this.selectedOverride)
    );

    if (this.selectedOverrideId) {
      this.isOverride = true;
      this.overrideDetails = {
        isActive: true,
        overrideId: this.selectedOverrideId,
        date: payload.weekColorDate,
        weekColor: this.selectedBgColor,
        weekColorName: payload.weekColor,
        location: this.licenseplate.warehouseLocationId,
        licensePlateId: this.licenseplate.id,
        licensePlateDesc: this.licenseplate.description,
        licensePlateStoreId: this.licensePlateStoreId || undefined,
        overrideDesc: payload.description
      };
    } else {
      this.isOverride = false;
      this.overrideDetails = {
        isActive: false,
        location: this.licenseplate.warehouseLocationId,
        licensePlateId: this.licenseplate.id,
        licensePlateDesc: this.licenseplate.description,
        licensePlateStoreId: this.licensePlateStoreId || undefined
      };
    }

    this.setWeekDate();
    localStorage.setItem('isOverride', this.isOverride.toString());
    localStorage.setItem(
      'overrideDetails',
      JSON.stringify(this.overrideDetails)
    );
    this.getCountOfLicensePlateItem();

    this.overrideStateChanged.emit(this.overrideDetails);
  }

  private getTouchedOverrideLicensePlates(): TouchedOverrideLicensePlate[] {
    try {
      return JSON.parse(
        localStorage.getItem('touchedOverrideLicensePlates') || '[]'
      );
    } catch {
      return [];
    }
  }

  private upsertTouchedOverrideLicensePlate(
    overrideId: number,
    licensePlateId: number
  ): void {
    const touched = this.getTouchedOverrideLicensePlates().filter(
      (t) => t.overrideId !== overrideId
    );
    touched.push({ overrideId, licensePlateId });
    localStorage.setItem(
      'touchedOverrideLicensePlates',
      JSON.stringify(touched)
    );
  }

  private removeTouchedOverrideLicensePlate(overrideId: number): void {
    const touched = this.getTouchedOverrideLicensePlates().filter(
      (t) => t.overrideId !== overrideId
    );
    localStorage.setItem(
      'touchedOverrideLicensePlates',
      JSON.stringify(touched)
    );
  }

  private async activateLicensePlateForOverride(
    overrideId: number,
    licensePlateId: number
  ): Promise<boolean> {
    try {
      await firstValueFrom(
        this.masterService.activateProductionOverrideLicensePlate(
          overrideId,
          licensePlateId
        )
      );
      this.upsertTouchedOverrideLicensePlate(overrideId, licensePlateId);
      return true;
    } catch (error) {
      const errorMessage = this.commonService.extractErrorMessage(
        error,
        'Failed to activate license plate'
      );
      this.toastr.error(errorMessage);
      return false;
    }
  }

  updateLicensePlateStatus(
    licensePlateId: number,
    status: LicensePlateStatus
  ): void {
    this.masterService
      .UpdateLicensePlateStatus(licensePlateId, status)
      .subscribe({
        next: () => {
          this.toastr.success('License plate status updated successfully');
        },
        error: (error) => {
          this.toastr.error('Failed to update license plate status');
          console.error(error);
        }
      });
  }
}
